// src/components/BankTransferList.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  getDocs,
  getDoc,
  doc,
  collection,
  updateDoc,
  arrayUnion,
  query,
  where,
  documentId,
} from "firebase/firestore";
import { db, auth } from "../api/firebase";
import {
  Box,
  Typography,
  Card,
  Chip,
  Stack,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  LinearProgress,
} from "@mui/material";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";

const functions = getFunctions();
const sendMail = httpsCallable(functions, "sendBankTransferConfirmation");

// 進捗表示：%（0〜100）
const calcPct = (done, total) => {
  const d = Number(done) || 0;
  const t = Number(total) || 0;
  if (t <= 0) return 0;
  const p = Math.floor((d / t) * 100);
  return Math.max(0, Math.min(100, p));
};

// ★ 金額フォーマッタ（NNN/NaN潰し）
const formatJPY = (value) => {
  if (value === null || value === undefined || value === "") return "未設定";

  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "未設定";
    return `¥${value.toLocaleString()}`;
  }

  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw) return "未設定";
    const n = Number(raw.replace(/,/g, ""));
    if (Number.isFinite(n)) return `¥${n.toLocaleString()}`;
    // 数字化できない場合はそのまま（運用次第で "未設定" にしてOK）
    return `¥${raw}`;
  }

  try {
    const s = String(value);
    return s ? `¥${s}` : "未設定";
  } catch {
    return "未設定";
  }
};

// 支払い判定：4期があれば paid、それ以外は unpaid
const derivePaymentStatus = (item) => {
  const phases = Array.isArray(item?.paymentPhases)
    ? item.paymentPhases.map((p) => String(p).trim()).filter(Boolean)
    : [];
  return phases.includes("4") ? "paid" : "unpaid";
};

// email normalize（大小/空白の揺れを潰す）
const normalizeEmail = (v) => {
  const s = String(v ?? "").trim().toLowerCase();
  return s || "";
};

// Firestore "in" は最大10件なのでチャンク化
const chunk10 = (arr) => {
  const a = Array.isArray(arr) ? arr : [];
  const out = [];
  for (let i = 0; i < a.length; i += 10) out.push(a.slice(i, i + 10));
  return out;
};

const BankTransferList = () => {
  const [groupedRequests, setGroupedRequests] = useState({});
  const [searchUserId, setSearchUserId] = useState("");
  const [checkedIds, setCheckedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(null);

  const [excludePhase4, setExcludePhase4] = useState(false);

  // ローディング＆進捗
  const [loading, setLoading] = useState(false);
  const [loadStage, setLoadStage] = useState("");
  const [loadTotal, setLoadTotal] = useState(0);
  const [loadDone, setLoadDone] = useState(0);

  // fetch の世代（StrictModeの二重実行や再取得連打で崩れない）
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.data();
        if (userData?.isAdmin) {
          setIsAdmin(true);
        } else {
          navigate("/home");
        }
      } else {
        navigate("/home");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const fetchRequests = async () => {
    const mySeq = ++fetchSeqRef.current;

    try {
      setLoading(true);
      setLoadStage("Firestore取得中");
      setLoadTotal(0);
      setLoadDone(0);

      const snapshot = await getDocs(collection(db, "bankTransferRequests2026"));
      if (mySeq !== fetchSeqRef.current) return;

      const rawData = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((d) => !!d.email && d.cancel !== true);

      // email で「最新だけ」に寄せる
      const latestByEmail = Object.values(
        rawData.reduce((acc, curr) => {
          const email = normalizeEmail(curr.email);
          const currTime = curr.requestedAt?.seconds || 0;
          if (!email) return acc;

          if (!acc[email] || (acc[email].requestedAt?.seconds || 0) < currTime) {
            acc[email] = { ...curr, email }; // normalized を保持
          }
          return acc;
        }, {})
      );

      if (mySeq !== fetchSeqRef.current) return;

      // ------------------------------------------------------------
      // ★ ユーザー情報補強：まず UID でまとめ取り（進捗が動く）
      //    rawData 全件の UID/Email を対象にする（全件タブ用）
      // ------------------------------------------------------------
      const uidToUser = new Map();
      const uids = Array.from(
        new Set(
          rawData
            .map((x) => String(x.userId ?? "").trim())
            .filter(Boolean)
        )
      );

      setLoadStage("ユーザー情報補強中（UID）");
      setLoadTotal(uids.length);
      setLoadDone(0);

      let uidDone = 0;

      for (const chunk of chunk10(uids)) {
        if (mySeq !== fetchSeqRef.current) return;
        if (chunk.length === 0) continue;

        try {
          const qUsers = query(
            collection(db, "users"),
            where(documentId(), "in", chunk)
          );
          const qs = await getDocs(qUsers);

          qs.forEach((docSnap) => {
            uidToUser.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
          });
        } catch (e) {
          console.error("UID enrich chunk failed:", e);
        } finally {
          uidDone += chunk.length; // ★ 成功/失敗に関わらず進捗を進める
          if (mySeq === fetchSeqRef.current) {
            setLoadDone(Math.min(uidDone, uids.length));
          }
        }
      }

      if (mySeq !== fetchSeqRef.current) return;

      // ------------------------------------------------------------
      // ★ 次に Email でも補強（userId無い/ズレの救済）
      // ------------------------------------------------------------
      const emailToUser = new Map();
      const emails = Array.from(
        new Set(rawData.map((x) => normalizeEmail(x.email)).filter(Boolean))
      );

      setLoadStage("ユーザー情報補強中（Email）");
      setLoadTotal(emails.length);
      setLoadDone(0);

      let emailDone = 0;

      for (const chunk of chunk10(emails)) {
        if (mySeq !== fetchSeqRef.current) return;
        if (chunk.length === 0) continue;

        try {
          const qUsersByEmail = query(
            collection(db, "users"),
            where("email", "in", chunk)
          );
          const qs = await getDocs(qUsersByEmail);

          qs.forEach((docSnap) => {
            const u = { id: docSnap.id, ...docSnap.data() };
            const e = normalizeEmail(u.email);
            if (e) emailToUser.set(e, u);
          });
        } catch (e) {
          console.error("Email enrich chunk failed:", e);
        } finally {
          emailDone += chunk.length;
          if (mySeq === fetchSeqRef.current) {
            setLoadDone(Math.min(emailDone, emails.length));
          }
        }
      }

      if (mySeq !== fetchSeqRef.current) return;

      // ------------------------------------------------------------
      // ★ latestByEmail に paymentPhases を付与（UID優先→Emailフォールバック）
      // ------------------------------------------------------------
      setLoadStage("一覧生成中");
      setLoadTotal(latestByEmail.length);
      setLoadDone(0);

      const enriched = latestByEmail.map((item, idx) => {
        const byUid = item.userId ? uidToUser.get(String(item.userId).trim()) : null;
        const byEmail = emailToUser.get(normalizeEmail(item.email)) || null;

        const u = byUid || byEmail;

        const phases =
          u?.payments && Array.isArray(u.payments)
            ? u.payments
                .map((p) => String(p?.paymentPhase ?? "").trim())
                .filter(Boolean)
            : [];

        // 進捗（マッピングは軽いので同期的に増やす）
        if (mySeq === fetchSeqRef.current) {
          setLoadDone(idx + 1);
        }

        return {
          ...item,
          // ★ 「取れていない」を減らすために userId を救済（任意）
          userId: item.userId || u?.id || null,
          paymentPhases: phases,
        };
      });

      if (mySeq !== fetchSeqRef.current) return;

      const sorted = enriched.sort((a, b) => {
        const timeA = a.requestedAt?.seconds || 0;
        const timeB = b.requestedAt?.seconds || 0;
        return timeB - timeA;
      });

      const grouped = sorted.reduce((acc, item) => {
        const date = new Date((item.requestedAt?.seconds || 0) * 1000);
        const dateStr = `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
        if (!acc[dateStr]) acc[dateStr] = [];
        acc[dateStr].push(item);
        return acc;
      }, {});

      setGroupedRequests(grouped);
    } catch (error) {
      console.error("取得エラー:", error);
    } finally {
      if (mySeq !== fetchSeqRef.current) return;
      setLoading(false);
      setLoadStage("");
      setLoadTotal(0);
      setLoadDone(0);
    }
  };

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchUserIds = useMemo(() => {
    return searchUserId
      .split("\n")
      .map((id) => id.trim())
      .filter((id) => id !== "");
  }, [searchUserId]);

  let totalFiltered = 0;
  const visibleIds = [];

  const handleCheck = (id) => {
    setCheckedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    const newIds = selectAll ? [] : [...visibleIds];
    setCheckedIds(newIds);
    setSelectAll(!selectAll);
  };

  const handleMarkAsPaid = async () => {
    const conflictMessages = [];

    // ✅ チェック: isPaid や phase:4 の重複
    for (const id of checkedIds) {
      const ref = doc(db, "bankTransferRequests2026", id);
      const snap = await getDoc(ref);
      const data = snap.data();
      const email = data?.email || "不明";

      if (data?.isPaid) {
        conflictMessages.push(`email: ${email} → 既に isPaid: true`);
        continue;
      }

      const userId = data?.userId;
      if (userId) {
        const userRef = doc(db, "users", userId);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.data();

        if (userData?.payments?.some((p) => String(p?.paymentPhase) === "4")) {
          conflictMessages.push(
            `email: ${email} → ユーザーに paymentPhase "4" が既に存在`
          );
          continue;
        }
      }
    }

    if (conflictMessages.length > 0) {
      alert("以下のドキュメントは処理できません:\n" + conflictMessages.join("\n"));
      return;
    }

    // ✅ データ更新 & メール送信
    for (const id of checkedIds) {
      const ref = doc(db, "bankTransferRequests2026", id);
      await updateDoc(ref, { isPaid: true });

      const snap = await getDoc(ref);
      const data = snap.data();
      const userId = data?.userId;
      const amount = data?.amount;

      if (userId && amount) {
        const userRef = doc(db, "users", userId);
        await updateDoc(userRef, {
          payments: arrayUnion({
            amountTotal: amount,
            paymentMethod: "銀行振込",
            paymentPhase: "4",
            timestamp: new Date(),
          }),
        });

        const userSnap = await getDoc(userRef);
        const userData = userSnap.data();

        try {
          await sendMail({
            email: userData.email,
            subject:
              "ご入金ありがとうございます！【マスターマインドメンバーズ事務局】",
            text: `
お忙しいなか、ご対応いただきありがとうございました。
参加費のご入金を確認させていただきましたので、ご報告となります。

＿＿＿＿＿

改めまして、マスターマインドメンバーズへようこそ！

是非、メンバーサイトへアクセスし、サイト内コンテンツをご活用ください。
＜メンバー専用サイト＞
https://mastermindmembers.net/

◎プログラムの開始は12/22を予定しております。
◎サイトは随時更新されます。
◎今後、事務局からのお知らせは事務局メール（info@mastermindmembers.net）より配信いたしますので、受信設定をお願いいたします。

その他ご不明な点等ございましたら、事務局までご連絡ください。
引き続き、どうぞ宜しくお願いいたします！

マスターマインドメンバーズ事務局
info@mastermindmembers.net
            `,
          });
        } catch (err) {
          console.error("メール送信失敗:", err);
        }
      }
    }

    alert("支払いステータスおよびユーザー支払い情報を更新しました。");
    fetchRequests();
  };

  // 1週間前チェック（色は変えるが、チェックボックスは無効化しない）
  const isOlderThan7Days = (timestamp) => {
    if (!timestamp?.seconds) return false;
    const requestedDate = new Date(timestamp.seconds * 1000);
    const now = new Date();
    const oneWeekAgo = new Date(now);
    oneWeekAgo.setDate(now.getDate() - 7);
    return requestedDate < oneWeekAgo;
  };

  if (isAdmin === null) return <Typography>認証確認中...</Typography>;
  if (!isAdmin) return null;

  const pct = calcPct(loadDone, loadTotal);

  return (
    <Box sx={{ px: 2 }}>
      {/* ローディング表示 */}
      {loading && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              {loadStage || "読み込み中"}
            </Typography>
            {loadTotal > 0 && (
              <Typography variant="body2" sx={{ opacity: 0.75 }}>
                （{loadDone} / {loadTotal}）
              </Typography>
            )}
            <Typography variant="body2" sx={{ opacity: 0.75, ml: "auto" }}>
              {pct}%
            </Typography>

            <Button
              size="small"
              variant="outlined"
              disabled
              sx={{ ml: 1, textTransform: "none" }}
            >
              読み込み中…
            </Button>
          </Stack>

          <LinearProgress variant="determinate" value={pct} />
        </Box>
      )}

      <TextField
        label="userIdで検索（改行可）"
        variant="outlined"
        size="small"
        fullWidth
        multiline
        minRows={3}
        sx={{ mb: 1 }}
        value={searchUserId}
        onChange={(e) => setSearchUserId(e.target.value)}
      />

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
        <Button
          variant="outlined"
          size="small"
          onClick={() => setSearchUserId("")}
        >
          クリア
        </Button>

        <Button
          variant="outlined"
          size="small"
          onClick={fetchRequests}
          disabled={loading}
        >
          再取得
        </Button>

        <FormControlLabel
          control={<Checkbox checked={selectAll} onChange={handleSelectAll} />}
          label="すべて選択"
        />

        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={checkedIds.length === 0 || loading}
          onClick={handleMarkAsPaid}
        >
          isPaid: true にする
        </Button>

        <FormControlLabel
          sx={{ ml: 1 }}
          control={
            <Checkbox
              checked={excludePhase4}
              onChange={(e) => setExcludePhase4(e.target.checked)}
            />
          }
          label="4期支払い者を含まない"
        />
      </Stack>

      {Object.entries(groupedRequests).map(([dateStr, items]) => {
        // ① userId 検索フィルタ
        let filteredItems =
          searchUserIds.length > 0
            ? items.filter((item) => searchUserIds.includes(item.userId))
            : items;

        // ② 「4期支払い者を含まない」がオンなら phase:"4" を持つ人を除外
        if (excludePhase4) {
          filteredItems = filteredItems.filter((item) => {
            const phases = item.paymentPhases || [];
            return !phases.includes("4");
          });
        }

        if (filteredItems.length === 0) return null;

        totalFiltered += filteredItems.length;
        visibleIds.push(...filteredItems.map((item) => item.id));

        return (
          <Box key={dateStr} sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              📅 {dateStr}（{filteredItems.length}人）
            </Typography>

            <Stack spacing={1}>
              {filteredItems.map((item, index) => {
                const rowIndex = index + 1;

                const time = new Date((item.requestedAt?.seconds || 0) * 1000);
                const amount = formatJPY(item.amount);

                const userId = item.userId || "No UID";
                const regName = item.registrationName || "未設定";
                const regPhone = item.registrationPhone || "不明";
                const formattedPhoneName = `${item.formattedPhone || "?"} ${regName}`;
                const email = item.email || "（email未設定）";

                const isOld = isOlderThan7Days(item.requestedAt);
                const derivedStatus = derivePaymentStatus(item);

                return (
                  <Card
                    key={item.id}
                    variant="outlined"
                    sx={{
                      p: 1,
                      backgroundColor: isOld
                        ? "#fff8dc" // 1週間超：薄い黄色
                        : derivedStatus === "paid"
                        ? "#e6f4ea"
                        : "#f9f9f9",
                      "& .MuiCardContent-root": { p: 0 },
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      sx={{ px: 1, flexWrap: "wrap" }}
                    >
                      {/* 1週間超でもチェック可能（disabledにしない） */}
                      <Checkbox
                        checked={checkedIds.includes(item.id)}
                        onChange={() => handleCheck(item.id)}
                        disabled={loading}
                      />

                      <Typography
                        variant="body2"
                        sx={{ width: 24, textAlign: "right", flexShrink: 0 }}
                      >
                        {rowIndex}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", flexShrink: 0 }}
                      >
                        {amount}
                      </Typography>

                      <Typography variant="body2" sx={{ flexShrink: 0 }}>
                        {userId}
                      </Typography>

                      <Typography variant="body2" sx={{ flexShrink: 0 }}>
                        {regName}（{regPhone}）
                      </Typography>

                      <Typography variant="body2" sx={{ flexShrink: 0 }}>
                        {formattedPhoneName}
                      </Typography>

                      <Typography variant="body2" sx={{ flexShrink: 0 }}>
                        🕒 {time.toLocaleTimeString()}
                      </Typography>

                      <Chip
                        label={derivedStatus}
                        size="small"
                        color={derivedStatus === "paid" ? "success" : "warning"}
                        sx={{ height: 20 }}
                      />

                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={{ flexShrink: 0 }}
                      >
                        <Typography variant="body2">phase: [</Typography>
                        {item.paymentPhases?.length > 0 ? (
                          item.paymentPhases.map((phase, i) =>
                            String(phase) === "4" ? (
                              <Box
                                key={i}
                                sx={{
                                  px: 0.5,
                                  borderRadius: 1,
                                  backgroundColor: "primary.light",
                                  color: "primary.contrastText",
                                  fontWeight: "bold",
                                  fontSize: "0.8rem",
                                  display: "inline-block",
                                }}
                              >
                                {phase}
                                {i < item.paymentPhases.length - 1 ? ", " : ""}
                              </Box>
                            ) : (
                              <Typography
                                key={i}
                                variant="body2"
                                sx={{ color: "text.primary" }}
                              >
                                {phase}
                                {i < item.paymentPhases.length - 1 ? ", " : ""}
                              </Typography>
                            )
                          )
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            なし
                          </Typography>
                        )}
                        <Typography variant="body2">]</Typography>
                      </Stack>

                      <Typography
                        variant="body2"
                        sx={{ flexShrink: 1, ml: "auto" }}
                      >
                        {email}
                      </Typography>
                    </Stack>
                  </Card>
                );
              })}
            </Stack>
          </Box>
        );
      })}

      {totalFiltered === 0 && !loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
          🔍 該当するユーザーIDのデータは見つかりませんでした。
        </Typography>
      )}
    </Box>
  );
};

export default BankTransferList;
